import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { FormattedMessage } from 'react-intl';
import { Service, useDataService } from 'services/data.service';
import { useExportRequest } from 'hooks/useExportRequest';
import { useContext, useState } from 'react';
import { TooManyRowsWarning } from 'pages/ArticleVisibility/components/MetricsCard/TooManyRowsWarning';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { format } from 'date-fns';
import { MetricsCardContent } from '../../model';
import { Tooltip } from 'components/Tooltip';
import { Button } from 'components/Button';

export type { MetricsCardContent };

interface MetricsCardProps {
  content: MetricsCardContent;
}

export function MetricsCard({ content }: MetricsCardProps) {
  // This constant controls the maximum number of rows to download and save to the target Excel file.
  const MAX_ROWS = 10000;
  const exportData = useExportRequest();

  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const actualRows = 0;

  const tenantConfig = useContext(TenantConfigContext);

  const { initiateExport } = useDataService(Service.ArticleVisibility);

  const startExport = (fileName: string) => {
    const timeLabel =
      exportData.creationTimeFilter?.fromDate && exportData.creationTimeFilter?.toDate
        ? `${exportData.creationTimeFilter?.fromDate.replace('-', '_')}-${exportData.creationTimeFilter.toDate}`
        : format(tenantConfig.currentDataVersion!, 'yyyyMMdd');

    const fullFileName = `${tenantConfig.name.replace(/\./g, ' ')}_${fileName}_table_${timeLabel}`;

    initiateExport(content.exportMetric ?? '', exportData).then(reportResponse => {
      if (reportResponse.data) {
        const fileURL = window.URL.createObjectURL(reportResponse.data);
        // Setting various property values
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = fullFileName;
        alink.click();
        window.URL.revokeObjectURL(fileURL);
      }
    });
  };

  return (
    <div
      className="pr-3 pl-5 py-3 flex flex-col gap-2 bg-surface-default relative rounded-lg h-40"
      data-testid={`av-cards-${content.cardId}`}
    >
      <TooManyRowsWarning
        open={exportErrorOpen}
        onClose={() => setExportErrorOpen(false)}
        maxRows={MAX_ROWS}
        actualRows={actualRows}
      />
      <div className="flex items-center justify-between w-full">
        <Tooltip tooltipContent={<FormattedMessage id={content.cardId} />}>
          <span className="text-base font-semibold text-text-placeholder mr-2 break-all line-clamp-2 hover:cursor-default">
            <FormattedMessage id={content.cardId} />
          </span>
        </Tooltip>
        {content.exportMetric ? (
          <Tooltip tooltipContent={<FormattedMessage id="insights.report.download" defaultMessage="Download" />}>
            <Button
              icon="Download"
              id={`download-${content.cardId}`}
              data-testid={`download-${content.cardId}`}
              aria-label="close"
              onClick={() => startExport(content.label)}
            />
          </Tooltip>
        ) : (
          ''
        )}
      </div>
      <div
        className="flex text-4xl font-bold flex-col justify-center h-full"
        data-testid={`av-cards-${content.cardId}-value`}
      >
        <MeasureFormat value={content.value} unit={MeasureUnitCode.RATE} />
      </div>
    </div>
  );
}
